import type { WeaponSkinDefinition, SkinRarity } from './weapon-skin-types';
import { WEAPON_SKIN_MANIFEST } from './weapon-skin-manifest';
import { KNIFE_GLOVE_SKINS } from './weapon-skin-manifest-knives';

/** Merge base weapon skins + knife/glove skins into a single map. */
function mergeManifests(...maps: ReadonlyMap<number, WeaponSkinDefinition>[]): Map<number, WeaponSkinDefinition> {
  const merged = new Map<number, WeaponSkinDefinition>();
  for (const m of maps) {
    for (const [id, skin] of m) merged.set(id, skin);
  }
  return merged;
}

export class WeaponSkinRegistry {
  private byId: Map<number, WeaponSkinDefinition>;
  private byWeapon: Map<string, WeaponSkinDefinition[]>;
  private byCollection: Map<string, WeaponSkinDefinition[]>;
  private byRarity: Map<SkinRarity, WeaponSkinDefinition[]>;

  constructor(skins: ReadonlyMap<number, WeaponSkinDefinition>) {
    // L22: Warn if registry is initialized with no skins
    if (skins.size === 0) {
      console.warn('WeaponSkinRegistry: initialized with empty manifest');
    }

    this.byId = new Map(skins);
    this.byWeapon = new Map();
    this.byCollection = new Map();
    this.byRarity = new Map();

    for (const skin of skins.values()) {
      // Index by weapon
      for (const weaponId of skin.weaponIds) {
        const existing = this.byWeapon.get(weaponId);
        if (existing) {
          existing.push(skin);
        } else {
          this.byWeapon.set(weaponId, [skin]);
        }
      }

      // Index by collection
      const colList = this.byCollection.get(skin.collection);
      if (colList) {
        colList.push(skin);
      } else {
        this.byCollection.set(skin.collection, [skin]);
      }

      // Index by rarity
      const rarList = this.byRarity.get(skin.rarity);
      if (rarList) {
        rarList.push(skin);
      } else {
        this.byRarity.set(skin.rarity, [skin]);
      }
    }
  }

  /** Get skin definition by ID, or undefined if unknown. */
  getSkin(id: number): WeaponSkinDefinition | undefined {
    return this.byId.get(id);
  }

  /** Get all skins available for a specific weapon. */
  getSkinsForWeapon(weaponId: string): WeaponSkinDefinition[] {
    return this.byWeapon.get(weaponId) ?? [];
  }

  /** Get all skins in a given collection. */
  getSkinsInCollection(collection: string): WeaponSkinDefinition[] {
    return this.byCollection.get(collection) ?? [];
  }

  /** Get all skins of a given rarity tier. */
  getSkinsByRarity(rarity: SkinRarity): WeaponSkinDefinition[] {
    return this.byRarity.get(rarity) ?? [];
  }

  /** Get all unique collection names. */
  getAllCollections(): string[] {
    return Array.from(this.byCollection.keys());
  }

  /** Get all skin definitions. */
  getAll(): WeaponSkinDefinition[] {
    return Array.from(this.byId.values());
  }

  /** Search skins by partial name match (case-insensitive). */
  search(query: string): WeaponSkinDefinition[] {
    if (!query) return [];
    const lower = query.toLowerCase();
    return this.getAll().filter(skin =>
      skin.name.toLowerCase().includes(lower) ||
      skin.weaponIds.some(w => w.toLowerCase().includes(lower)) ||
      skin.collection.toLowerCase().includes(lower),
    );
  }
}

export const weaponSkinRegistry = new WeaponSkinRegistry(
  mergeManifests(WEAPON_SKIN_MANIFEST, KNIFE_GLOVE_SKINS),
);
